import React, { useMemo } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { ExternalLink, Pencil, Trash2, ArrowUp, ArrowDown, ArrowUpDown, Paperclip, Calculator } from "lucide-react";
import LinkWithPreview from "@/components/LinkWithPreview";
import { computeDuplicates } from "@/lib/duplicates";
import { sortRows } from "@/lib/sortRows";
import { useSettings } from "@/context/SettingsContext";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
export { scostBand } from "@/lib/scostBands";

const fmtEuro = (n) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n || 0);
const fmtNum = (n, d = 0) => new Intl.NumberFormat("it-IT", { maximumFractionDigits: d, minimumFractionDigits: d }).format(n || 0);
const fmtPct = (n) => (n === null || n === undefined) ? "—" : `${(n * 100).toFixed(1)}%`;

const NUMERIC_FIELDS = new Set(["mq", "prezzo", "eur_mq", "scostamento"]);

const DuplicateBadge = ({ dup, onDismiss, prop }) => {
  if (!dup) return null;
  const certain = dup.level === "red";
  const title = certain
    ? `Duplicato certo — ${dup.twins.length} annuncio/i con TUTTI i campi uguali. Clicca per confermare che NON è un duplicato.`
    : `Possibile duplicato — ${dup.count} campi in comune con ${dup.twins.length} annuncio/i. Clicca per confermare che NON è un duplicato.`;
  if (!onDismiss) {
    return (
      <span
        className={`relative inline-flex items-center justify-center rounded-full ${certain ? "ring-2 ring-[#FF3B30] p-0.5" : ""}`}
        title={title}
        data-testid={`dup-${dup.level}`}
      >
        <img src="/board-icons/flame.png" alt="" aria-hidden="true" className="w-5 h-5 object-contain" />
      </span>
    );
  }
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onDismiss(prop); }}
      className={`relative inline-flex items-center justify-center rounded-full transition-transform hover:scale-110 focus:outline-none focus:ring-2 focus:ring-[#007AFF] cursor-pointer ${certain ? "ring-2 ring-[#FF3B30] p-0.5" : ""}`}
      title={title}
      aria-label="Segnala come non duplicato"
      data-testid={`dup-${dup.level}-${prop?.id}`}
    >
      <img src="/board-icons/flame.png" alt="" aria-hidden="true" className="w-5 h-5 object-contain" />
    </button>
  );
};

const SORT_LABELS = {
  note: "Note", citta: "Città", macro_area: "Macro area", indirizzo: "Indirizzo",
  piano: "Piano", mq: "MQ", prezzo: "Prezzo", eur_mq: "€/MQ",
  da_ristrutturare: "Ristrutturato", ascensore: "Asc.", venditore: "Venditore", scostamento: "Scostam.",
};

const ScostBadge = ({ v, source, scostBand }) => {
  const b = scostBand(v);
  if (v === null || v === undefined) return <span className="text-slate-400 text-xs">—</span>;
  return (
    <div className="flex items-center justify-end gap-1.5">
      <Badge variant="outline" className="rounded-full px-2 py-0.5 text-[11px] font-mono-num border"
        style={{ color: b.color, borderColor: b.color, background: `${b.color}12` }}>{fmtPct(v)}</Badge>
      {source === "manuale" && <span className="text-[9px] uppercase tracking-widest text-slate-400">M</span>}
    </div>
  );
};

const YNBadge = ({ v }) => (
  <Badge variant="outline" className={`rounded-full text-[10px] px-2 py-0 uppercase tracking-wider border-0 ${v ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-500"}`}>
    {v ? "Sì" : "No"}
  </Badge>
);

const TriBadge = ({ v }) => {
  const s = v === true ? "SI" : v === false ? "NO" : (v || "NI");
  if (s === "SI") return <Badge variant="outline" className="rounded-full text-[10px] px-2 py-0 uppercase tracking-wider border-0 bg-slate-900 text-white">Sì</Badge>;
  if (s === "NO") return <Badge variant="outline" className="rounded-full text-[10px] px-2 py-0 uppercase tracking-wider border-0 bg-slate-100 text-slate-500">No</Badge>;
  return <Badge variant="outline" className="rounded-full text-[10px] px-2 py-0 uppercase tracking-wider border-0 bg-slate-200 text-slate-600">NI</Badge>;
};

const NoteCell = ({ note }) => {
  if (!note) return null;
  return (
    <div className="relative group inline-flex items-center justify-center">
      <img src="/board-icons/pencil.png" alt="" className="w-5 h-5 object-contain" aria-hidden="true" />
      <div className="pointer-events-none absolute left-0 top-full mt-1 hidden group-hover:block z-50 w-64 bg-slate-900 text-white text-[11px] rounded-lg px-3 py-2 shadow-xl whitespace-pre-wrap leading-relaxed">
        {note}
      </div>
    </div>
  );
};

const SortHeader = ({ label, field, sortConfig, onSort, align = "left", icon = null, title }) => {
  const idx = sortConfig.findIndex((s) => s.key === field);
  const entry = idx >= 0 ? sortConfig[idx] : null;
  const active = !!entry;
  const Icon = !active ? ArrowUpDown : entry.direction === "asc" ? ArrowUp : ArrowDown;
  const accessibleName = title || (typeof label === "string" ? label : field);
  const titleHint = active
    ? `Ordinato ${entry.direction === "asc" ? "crescente" : "decrescente"} (priorità ${idx + 1}). Clic per invertire, Shift+Clic per rimuovere.`
    : `Clic per ordinare per ${accessibleName}. Shift+Clic per aggiungere ordinamento secondario.`;
  return (
    <th className={align === "right" ? "num" : ""}>
      <button
        onClick={(e) => onSort(field, e.shiftKey)}
        data-testid={`sort-${field}`}
        className={`inline-flex items-center gap-1 hover:text-[#007AFF] transition-colors ${active ? "text-[#007AFF]" : ""} ${align === "right" ? "flex-row-reverse" : ""}`}
        title={titleHint}
        aria-label={accessibleName}
      >
        {icon ? icon : <span>{label}</span>}
        <Icon className={`w-3 h-3 ${active ? "opacity-100" : "opacity-40"}`} />
        {active && sortConfig.length > 1 && (
          <span
            className="ml-0.5 inline-flex items-center justify-center h-4 min-w-[16px] px-1 rounded-full bg-[#007AFF] text-white text-[9px] font-mono-num leading-none"
            data-testid={`sort-priority-${field}`}
          >
            {idx + 1}
          </span>
        )}
      </button>
    </th>
  );
};

const PropertiesTable = ({
  properties, loading, onEdit, onDelete, canWrite, onFocus, onToggleFavorite,
  onDismissDuplicate,
  duplicates: extDuplicates,
  sortConfig, onSortChange,
  selectedIds, onToggleSelected, onToggleSelectAll,
}) => {
  const localDup = useMemo(() => (extDuplicates ? new Map() : computeDuplicates(properties)), [properties, extDuplicates]);
  const duplicates = extDuplicates || localDup;
  const selection = selectedIds || new Set();
  const selectable = !!(canWrite && onToggleSelected);
  const { scostBand } = useSettings();
  const { user } = useAuth();
  // Any active subscriber (or super-admin) can use the "Piano Economico"
  // shortcut on any property they can access. The old check only allowed
  // `tier === "pro"` which broke after the Feb 2026 migration to the new
  // taxonomy (bronze/silver/gold).
  const isPro = user?.role === "admin" || user?.status === "subscriber";

  const addToPiano = async (p) => {
    try {
      await api.post(`/piano/plans?property_id=${p.id}`);
      toast.success("Aggiunto al Piano Economico");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore aggiunta al Piano");
    }
  };

  const handleSort = (field, additive) => {
    const prev = sortConfig || [];
    const idx = prev.findIndex((s) => s.key === field);
    const defaultDir = NUMERIC_FIELDS.has(field) ? "desc" : "asc";
    const nextForField = (() => {
      if (idx === -1) return { key: field, direction: defaultDir };
      const cur = prev[idx].direction;
      if (cur === defaultDir) return { key: field, direction: defaultDir === "asc" ? "desc" : "asc" };
      return null;
    })();

    let next;
    if (additive) {
      if (idx === -1) next = nextForField ? [...prev, nextForField] : prev;
      else if (nextForField === null) next = prev.filter((_, i) => i !== idx);
      else { next = [...prev]; next[idx] = nextForField; }
    } else {
      next = nextForField === null ? [] : [nextForField];
    }
    onSortChange && onSortChange(next);
  };

  const clearSort = () => onSortChange && onSortChange([]);

  const sorted = useMemo(() => sortRows(properties, sortConfig || []), [properties, sortConfig]);

  // Header select-all state: none / some / all of the CURRENT sorted view.
  const allSelected = selectable && sorted.length > 0 && sorted.every((p) => selection.has(p.id));
  const someSelected = selectable && !allSelected && sorted.some((p) => selection.has(p.id));

  return (
    <div className="ios-card overflow-hidden" data-testid="properties-table-wrapper">
      {(sortConfig || []).length > 0 && (
        <div className="flex items-center justify-between px-4 py-2 border-b border-slate-100 bg-slate-50/60 text-[11px]">
          <div className="flex items-center gap-2 flex-wrap text-slate-600">
            <span className="uppercase tracking-widest text-slate-500 font-semibold">Ordinamento</span>
            {sortConfig.map((s, i) => (
              <span key={s.key} className="inline-flex items-center gap-1 bg-white border border-slate-200 rounded-full px-2 py-0.5 font-mono-num" data-testid={`sort-chip-${s.key}`}>
                <span className="text-[9px] text-slate-400">{i + 1}.</span>
                <span className="text-slate-700">{SORT_LABELS[s.key] || s.key}</span>
                {s.direction === "asc" ? <ArrowUp className="w-3 h-3 text-[#007AFF]" /> : <ArrowDown className="w-3 h-3 text-[#007AFF]" />}
              </span>
            ))}
            <span className="text-slate-400 hidden md:inline">· Shift+Clic per aggiungere colonne</span>
          </div>
          <Button variant="ghost" size="sm" className="h-7 rounded-full text-[11px] text-slate-500 hover:text-[#007AFF]" onClick={clearSort} data-testid="sort-clear">
            Azzera ordinamento
          </Button>
        </div>
      )}
      <div className="max-h-[calc(100vh-24rem)] overflow-auto">
        <table className="w-full data-table" data-testid="properties-table">
          <thead>
            <tr>
              {selectable && (
                <th className="w-8 px-0 text-center" data-testid="col-select-header">
                  <Checkbox
                    checked={allSelected ? true : (someSelected ? "indeterminate" : false)}
                    onCheckedChange={(v) => onToggleSelectAll && onToggleSelectAll(sorted.map((p) => p.id), !!v)}
                    className="h-4 w-4 rounded border-slate-300 data-[state=checked]:bg-[#007AFF] data-[state=checked]:border-[#007AFF]"
                    aria-label="Seleziona tutti"
                    data-testid="select-all-checkbox"
                  />
                </th>
              )}
              <th className="w-7 px-0 text-[10px] uppercase tracking-wider text-slate-500 font-semibold text-center">Pref.</th>
              <th className="w-7 px-0 text-[10px] uppercase tracking-wider text-slate-500 font-semibold text-center">PIN</th>
              <th className="w-6 px-0 text-[10px] uppercase tracking-wider text-slate-500 font-semibold text-center">Dup.</th>
              <th className="w-6 px-0 text-[10px] uppercase tracking-wider text-slate-500 font-semibold text-center">Note</th>
              <SortHeader label="Città" field="citta" sortConfig={sortConfig || []} onSort={handleSort} />
              <SortHeader label="Macro area" field="macro_area" sortConfig={sortConfig || []} onSort={handleSort} />
              <SortHeader label="Indirizzo" field="indirizzo" sortConfig={sortConfig || []} onSort={handleSort} />
              <SortHeader label="Piano" field="piano" sortConfig={sortConfig || []} onSort={handleSort} />
              <SortHeader label="MQ" field="mq" sortConfig={sortConfig || []} onSort={handleSort} align="right" />
              <SortHeader label="Prezzo" field="prezzo" sortConfig={sortConfig || []} onSort={handleSort} align="right" />
              <SortHeader label="€/MQ" field="eur_mq" sortConfig={sortConfig || []} onSort={handleSort} align="right" />
              <SortHeader label="Ristrutturato" field="da_ristrutturare" sortConfig={sortConfig || []} onSort={handleSort} />
              <SortHeader label="Asc." field="ascensore" sortConfig={sortConfig || []} onSort={handleSort} />
              <SortHeader label="Venditore" field="venditore" sortConfig={sortConfig || []} onSort={handleSort} />
              <SortHeader label="Scostam." field="scostamento" sortConfig={sortConfig || []} onSort={handleSort} align="right" />
              <th className="w-6 px-0 text-[10px] uppercase tracking-wider text-slate-500 font-semibold text-center">Link</th>
              <th className="w-7 px-0 text-[10px] uppercase tracking-wider text-slate-500 font-semibold text-center" title="Allegati">Alleg.</th>
              <th className="w-14"></th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={selectable ? 19 : 18} className="py-10 text-center text-slate-500 text-sm">Caricamento…</td></tr>}
            {!loading && sorted.length === 0 && (
              <tr><td colSpan={selectable ? 19 : 18} className="py-10 text-center text-slate-500 text-sm" data-testid="empty-state">
                Nessun immobile trovato. Aggiungi il primo o importa da Excel/CSV.
              </td></tr>
            )}
            {!loading && sorted.map((p) => {
              const b = scostBand(p.scostamento);
              const isSel = selection.has(p.id);
              return (
                <tr key={p.id} data-testid={`property-row-${p.id}`} className={`${b.row} ${isSel ? "bg-[#007AFF]/5" : ""}`}>
                  {selectable && (
                    <td className="w-8 px-0 text-center" data-testid={`select-cell-${p.id}`}>
                      <Checkbox
                        checked={isSel}
                        onCheckedChange={(v) => onToggleSelected(p.id, !!v)}
                        className="h-4 w-4 rounded border-slate-300 data-[state=checked]:bg-[#007AFF] data-[state=checked]:border-[#007AFF]"
                        aria-label={`Seleziona ${p.indirizzo}`}
                        data-testid={`select-${p.id}`}
                      />
                    </td>
                  )}
                  <td className="py-2 pl-2 pr-1 w-9 text-center">
                    {canWrite ? (
                      <button
                        type="button"
                        onClick={() => onToggleFavorite && onToggleFavorite(p)}
                        data-testid={`favorite-${p.id}`}
                        title={p.is_favorite ? "Rimuovi dai preferiti — la riga rispetterà di nuovo i filtri" : "Aggiungi ai preferiti — la riga resterà visibile anche fuori dai filtri"}
                        className="inline-flex items-center justify-center h-7 w-7 rounded-full hover:bg-slate-100 transition-colors"
                      >
                        <img src="/board-icons/star.png" alt="" aria-hidden="true" className={`w-5 h-5 object-contain transition-opacity ${p.is_favorite ? "opacity-100" : "opacity-20"}`} />
                      </button>
                    ) : (
                      <span className="inline-flex items-center justify-center h-7 w-7 rounded-full" title={p.is_favorite ? "Preferito" : ""}>
                        <img src="/board-icons/star.png" alt="" aria-hidden="true" className={`w-5 h-5 object-contain ${p.is_favorite ? "opacity-100" : "opacity-20"}`} />
                      </span>
                    )}
                  </td>
                  <td className="py-2 pl-2 pr-1 w-9 text-center">
                    {p.lat && p.lon ? (
                      <button
                        type="button"
                        onClick={() => onFocus && onFocus(p)}
                        data-testid={`focus-${p.id}`}
                        title="Centra la mappa su questo immobile"
                        className="inline-flex items-center justify-center h-7 w-7 rounded-full hover:bg-slate-100 transition-colors"
                      >
                        <img src="/board-icons/target.png" alt="" aria-hidden="true" className="w-5 h-5 object-contain opacity-100" />
                      </button>
                    ) : (
                      <span className="inline-flex items-center justify-center h-7 w-7 rounded-full cursor-not-allowed" title="Nessuna posizione geografica per questo immobile">
                        <img src="/board-icons/target.png" alt="" aria-hidden="true" className="w-5 h-5 object-contain opacity-20" />
                      </span>
                    )}
                  </td>
                  <td className="py-2 px-0 w-8 text-center" data-testid={`dup-cell-${p.id}`}>
                    <DuplicateBadge dup={duplicates.get(p.id)} prop={p} onDismiss={canWrite ? onDismissDuplicate : undefined} />
                  </td>
                  <td className="py-2"><NoteCell note={p.note} /></td>
                  <td className="uppercase text-xs">{p.citta}</td>
                  <td className="uppercase text-xs text-slate-600">{p.macro_area}</td>
                  <td className="font-medium uppercase">
                    <span>{p.indirizzo}</span>
                    {p.civico ? (
                      <span className="text-slate-500 font-mono-num">, {p.civico}</span>
                    ) : (
                      <span
                        className="ml-1.5 inline-flex items-center justify-center w-4 h-4 rounded-full bg-amber-100 text-amber-700 text-[9px] font-bold border border-amber-300 shrink-0 align-middle"
                        title="Numero civico mancante — clicca la matita per aggiungerlo"
                        data-testid={`missing-civico-${p.id}`}
                        aria-label="Civico mancante"
                      >
                        ?
                      </span>
                    )}
                  </td>
                  <td className="text-slate-600 text-xs text-center">{p.piano || "—"}</td>
                  <td className="num">{fmtNum(p.mq)}</td>
                  <td className="num font-semibold">{fmtEuro(p.prezzo)}</td>
                  <td className="num text-slate-700">{fmtEuro(p.eur_mq)}</td>
                  <td className="text-center"><TriBadge v={p.da_ristrutturare} /></td>
                  <td><YNBadge v={p.ascensore} /></td>
                  <td className="text-xs">
                    {p.tipo_venditore === "PRIVATO" ? <span className="text-slate-700">Privato</span>
                      : <span className="text-slate-700">{p.agenzia_nome || "Agenzia"}</span>}
                  </td>
                  <td className="num"><ScostBadge v={p.scostamento} source={p.scostamento_source} scostBand={scostBand} /></td>
                  <td>
                    {p.link ? (
                      <LinkWithPreview href={p.link} preview={p.preview_image} testId={`link-${p.id}`} title={p.indirizzo} position="right">
                        <ExternalLink className="w-3.5 h-3.5" />
                      </LinkWithPreview>
                    ) : <span className="text-slate-300">—</span>}
                  </td>
                  <td className="text-center px-0" data-testid={`attach-cell-${p.id}`}>
                    {Array.isArray(p.attachments) && p.attachments.length > 0 ? (
                      <span className="inline-flex items-center justify-center gap-0.5 text-slate-700" title={`${p.attachments.length} allegato/i`}>
                        <Paperclip className="w-3.5 h-3.5" strokeWidth={1.75} />
                        <span className="text-[10px] font-mono-num text-slate-500">{p.attachments.length}</span>
                      </span>
                    ) : (
                      <span className="text-slate-200" aria-hidden="true">
                        <Paperclip className="w-3.5 h-3.5 opacity-20" strokeWidth={1.75} />
                      </span>
                    )}
                  </td>
                  <td>
                    <div className="flex items-center gap-1">
                      {isPro && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 w-7 p-0 rounded-full text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                          onClick={() => addToPiano(p)}
                          data-testid={`piano-add-${p.id}`}
                          title="Aggiungi al Piano Economico"
                        >
                          <Calculator className="w-3.5 h-3.5" />
                        </Button>
                      )}
                      {canWrite && (
                        <>
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0 rounded-full" onClick={() => onEdit(p)} data-testid={`edit-${p.id}`}>
                            <Pencil className="w-3.5 h-3.5" />
                          </Button>
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0 rounded-full text-rose-600 hover:text-rose-700 hover:bg-rose-50" onClick={() => onDelete(p)} data-testid={`delete-${p.id}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PropertiesTable;
